/**
 * FacilityFormModal.tsx — Admin > Facilities, add and edit.
 *
 * The corporate dashboard's facility modal (app/dashboard/facilities/
 * facility-modals.tsx) for one account, in its order:
 *   - the Facility Image tile (suggested) beside Default + Status, Facility
 *     Name *, Address, City / State / Postal Code / Country, Phone Number and
 *     Timezone (the timezones of the chosen country);
 *   - Facility type and Open date, Soft open start / stop, the collapsible
 *     Social URLs & Website, Operating Hours (Apply to calendar), the Care Team
 *     (labelled with the account's own role titles), Shortcode Address, Hint;
 *   - then collapsible sections, all closed until clicked: Financial and
 *     Display Settings, Text Numbers, Rates (Increment, Max, Generate rates and
 *     Load fees from facility on top, then an operating room column, blue, on
 *     the left and an anesthesia column, amber, on the right, each with its
 *     "$ per hours" price and its cost table) and IP Addresses.
 * Address, city, state, postal code, country, phone, open date and the image
 * are "— suggested" (amber) while empty; only the name is required. Changed
 * fields carry corp's "modified" pill with a revert (on a closed section's
 * header too), and the footer has Unsaved changes + Revert. There is no
 * Delete in the modal.
 *
 * The rate sections are the legacy FTP facility modal's: Generate Rates fills
 * both cost tables from "$ per hours", the Increment and the Max hours
 * (generateRateRows), and Load copies another live facility's tables. As in
 * legacy a table's hours are not typed: Generate and Add row set them from the
 * Increment, and only the cost is edited. The prices typed into the generator
 * are never saved, as in legacy; the tables, Increment and Max are. An edit
 * sends only the fields that changed (all
 * three care-team members together, because the API resets any it is not
 * sent; a cost table as a whole, because the API replaces its rows), so
 * opening and saving a facility never writes default hours, a timezone or
 * rates it did not have. A new facility starts in the account's country and
 * timezone.
 *
 * On a Locked page (useAdminPageLevel) it is a viewer, silently (R18): the
 * title is "Facility", the fields sit in an AdminLockedFieldset (values shown,
 * nothing changes, no "— suggested"), the hours read as times, the image can
 * be viewed but not changed, the sections still open, IP addresses and cost
 * tables have no Add or Remove, there is nothing to generate or load, and the
 * footer is a single Close that never asks to discard.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Music,
  Pin,
  Plus,
  RotateCcw,
  Trash2,
  Twitter,
  Youtube,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { TimeField, formatTime12h } from '@/components/ui/TimeField';
import {
  DESCRIPTION_CLASS,
  DialogField,
  DialogFooterLayout,
  DialogHeading,
  LABEL_CLASS,
  ModifiedPill,
  SwitchField,
  fieldClasses,
  type FieldState,
} from '@/components/admin/DialogChrome';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useTimezones } from '@/hooks/useTimezones';
import { buildStaffOptions, toIncludeIds } from '@/lib/staffOptions';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { listCoordinators, formatCoordinatorName } from '@/services/coordinators.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { listNurses, formatNurseName } from '@/services/nurses.service';
import { listCountryCodes } from '@/services/patients.service';
import {
  DAYS_OF_WEEK,
  FACILITY_RATE_TYPES,
  FACILITY_STATUS_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  HOUR_STEP_OPTIONS,
  createFacility,
  defaultOperatingHours,
  generateRateRows,
  getAccountFacilityDefaults,
  listCountryTimezones,
  nextRateRow,
  serializeRateArray,
  updateFacility,
  type FacilityPayload,
  type FacilityRecord,
  type FacilityStatus,
  type OperatingHours,
  type RateRow,
  type TimezoneChoice,
} from '@/services/facilities.service';
import { toast } from '@/store/toastStore';
import { FacilityTextNumbers } from './FacilityTextNumbers';

// ─── Form ──────────────────────────────────────────────────────────────────────

export interface FacilityForm {
  name: string;
  defaultFacility: boolean;
  facilityStatus: FacilityStatus;
  facilityType: number;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryID: number | null;
  phone: string;
  /** null until one is chosen. */
  timezoneID: number | null;
  openDate: string;
  softOpenStart: string;
  softOpenStop: string;
  website: string;
  facebook: string;
  twitter: string;
  instagramUrl: string;
  pinterestUrl: string;
  linkedinUrl: string;
  tiktokUrl: string;
  youtubeUrl: string;
  operatingHours: OperatingHours;
  applyHoursToCalendar: boolean;
  doctorID: number;
  nurseID: number;
  coordID: number;
  address: string;
  hint: string;
  taxRate: string;
  onQuotes: boolean;
  nonAdminAllowEdit: boolean;
  onNewLeadForm: boolean;
  facilityRateType: number;
  hourIncrement: string;
  maxHours: string;
  orRates: RateRow[];
  anRates: RateRow[];
  ipAddresses: string[];
  facilityImage: string;
}

type FormKey = keyof FacilityForm;

/** A new facility, as corp's create modal starts it. */
export function emptyFacilityForm(): FacilityForm {
  return {
    name: '',
    defaultFacility: false,
    facilityStatus: 'closed',
    facilityType: 0,
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    countryID: null,
    phone: '',
    timezoneID: null,
    openDate: '',
    softOpenStart: '',
    softOpenStop: '',
    website: '',
    facebook: '',
    twitter: '',
    instagramUrl: '',
    pinterestUrl: '',
    linkedinUrl: '',
    tiktokUrl: '',
    youtubeUrl: '',
    operatingHours: defaultOperatingHours(),
    applyHoursToCalendar: false,
    doctorID: 0,
    nurseID: 0,
    coordID: 0,
    address: '',
    hint: '',
    taxRate: '',
    onQuotes: false,
    nonAdminAllowEdit: false,
    onNewLeadForm: false,
    facilityRateType: 0,
    hourIncrement: '0.25',
    maxHours: '',
    orRates: [],
    anRates: [],
    ipAddresses: [],
    facilityImage: '',
  };
}

/** A stored increment as its option ("0.50" is "0.5"), so the select shows it; none reads as legacy's first step. */
function hourStep(v: string): string {
  if (v === '') return HOUR_STEP_OPTIONS[0];
  return HOUR_STEP_OPTIONS.find((o) => Number(o) === Number(v)) ?? v;
}

export function formFromFacility(f: FacilityRecord): FacilityForm {
  return {
    name: f.name,
    defaultFacility: f.defaultFacility,
    facilityStatus: f.facilityStatus,
    facilityType: f.facilityType,
    address2: f.address2,
    city: f.city,
    state: f.state,
    postalCode: f.postalCode,
    countryID: f.countryID,
    phone: f.phone,
    timezoneID: f.timezoneID > 0 ? f.timezoneID : null,
    openDate: f.openDate,
    softOpenStart: f.softOpenStart,
    softOpenStop: f.softOpenStop,
    website: f.website,
    facebook: f.facebook,
    twitter: f.twitter,
    instagramUrl: f.instagramUrl,
    pinterestUrl: f.pinterestUrl,
    linkedinUrl: f.linkedinUrl,
    tiktokUrl: f.tiktokUrl,
    youtubeUrl: f.youtubeUrl,
    operatingHours: f.operatingHours ?? defaultOperatingHours(),
    applyHoursToCalendar: f.applyHoursToCalendar,
    doctorID: f.doctorID,
    nurseID: f.nurseID,
    coordID: f.coordID,
    address: f.address,
    hint: f.hint,
    taxRate: f.taxRate,
    onQuotes: f.onQuotes,
    nonAdminAllowEdit: f.nonAdminAllowEdit,
    onNewLeadForm: f.onNewLeadForm,
    facilityRateType: f.facilityRateType,
    hourIncrement: hourStep(f.hourIncrement),
    maxHours: f.maxHours,
    orRates: f.orRates.map((r) => ({ ...r })),
    anRates: f.anRates.map((r) => ({ ...r })),
    ipAddresses: [...f.ipAddresses],
    facilityImage: f.facilityImage,
  };
}

const ZERO_DATE = '0000-00-00';
const TEAM_KEYS: FormKey[] = ['doctorID', 'nurseID', 'coordID'];
const SOCIAL_KEYS: FormKey[] = ['website', 'facebook', 'twitter', 'instagramUrl', 'pinterestUrl', 'linkedinUrl', 'tiktokUrl', 'youtubeUrl'];

/** What one form field sends. Facebook and Twitter go to both of the API's columns. */
function fragment(key: FormKey, f: FacilityForm): FacilityPayload {
  switch (key) {
    case 'name':
      return { name: f.name.trim() };
    case 'countryID':
      return f.countryID ? { countryID: f.countryID } : {};
    case 'timezoneID':
      return { timezoneID: f.timezoneID ?? 0 };
    case 'openDate':
    case 'softOpenStart':
    case 'softOpenStop':
      return { [key]: f[key] || ZERO_DATE };
    case 'facebook':
      return { facebook: f.facebook.trim(), facebookUrl: f.facebook.trim() };
    case 'twitter':
      return { twitter: f.twitter.trim(), twitterUrl: f.twitter.trim() };
    case 'taxRate':
      return { taxRate: Number(f.taxRate) || 0 };
    case 'orRates':
      return { orArray: serializeRateArray(f.orRates) };
    case 'anRates':
      return { anArray: serializeRateArray(f.anRates) };
    case 'ipAddresses':
      return { ipAddresses: f.ipAddresses.map((ip) => ip.trim()).filter(Boolean) };
    case 'facilityImage':
      return f.facilityImage ? { facilityImage: f.facilityImage } : {};
    default: {
      const v = f[key];
      return { [key]: typeof v === 'string' ? v.trim() : v } as FacilityPayload;
    }
  }
}

/** Everything a new facility is created with (corp's create payload). */
export function buildCreatePayload(f: FacilityForm): FacilityPayload {
  const out: FacilityPayload = {};
  for (const key of Object.keys(f) as FormKey[]) Object.assign(out, fragment(key, f));
  return {
    ...out,
    countryID: f.countryID ?? 1,
    phoneCountryID: f.countryID ?? undefined,
    closeDate: ZERO_DATE,
    active: true,
  };
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function changedKeys(form: FacilityForm, saved: FacilityForm): Set<FormKey> {
  return new Set((Object.keys(form) as FormKey[]).filter((k) => k !== 'facilityImage' && !same(form[k], saved[k])));
}

/** Only what changed since the form loaded. */
export function buildUpdatePayload(form: FacilityForm, saved: FacilityForm): FacilityPayload {
  const keys = changedKeys(form, saved);
  if (TEAM_KEYS.some((k) => keys.has(k))) TEAM_KEYS.forEach((k) => keys.add(k));
  const out: FacilityPayload = {};
  keys.forEach((k) => Object.assign(out, fragment(k, form)));
  return out;
}

// ─── Labels and options ────────────────────────────────────────────────────────

const FIELD_LABELS: Record<FormKey, string> = {
  name: 'Facility Name',
  defaultFacility: 'Default',
  facilityStatus: 'Status',
  facilityType: 'Facility type',
  address2: 'Address',
  city: 'City',
  state: 'State',
  postalCode: 'Postal Code',
  countryID: 'Country',
  phone: 'Phone Number',
  timezoneID: 'Timezone',
  openDate: 'Open date',
  softOpenStart: 'Soft open start',
  softOpenStop: 'Soft open stop',
  website: 'Website',
  facebook: 'Facebook',
  twitter: 'Twitter / X',
  instagramUrl: 'Instagram',
  pinterestUrl: 'Pinterest',
  linkedinUrl: 'LinkedIn',
  tiktokUrl: 'TikTok',
  youtubeUrl: 'YouTube',
  operatingHours: 'Operating Hours',
  applyHoursToCalendar: 'Apply to calendar',
  doctorID: 'Doctor',
  nurseID: 'Nurse',
  coordID: 'Coordinator',
  address: 'Shortcode Address',
  hint: 'Hint',
  taxRate: 'Sales Tax Rate',
  onQuotes: 'Display on quotes & payments',
  nonAdminAllowEdit: 'Allow non-admins to change rates on quotes',
  onNewLeadForm: 'Display on New Lead Form',
  facilityRateType: 'Facility Rates',
  hourIncrement: 'Increment',
  maxHours: 'Max',
  orRates: 'Operating Room Cost Table',
  anRates: 'Anesthesia Cost Table',
  ipAddresses: 'IP Addresses',
  facilityImage: 'Facility Image',
};

const FINANCIAL_KEYS: FormKey[] = ['taxRate', 'onQuotes', 'nonAdminAllowEdit', 'onNewLeadForm', 'facilityRateType'];

/** Corp's status colours, on the list's pill and the modal's status select. */
export const FACILITY_STATUS_CLASS: Record<FacilityStatus, string> = {
  open: 'bg-green-100 text-green-800',
  'soft-open': 'bg-blue-100 text-blue-800',
  presales: 'bg-amber-100 text-amber-800',
  closed: 'bg-red-100 text-red-800',
};

const YES_NO = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
];

const SOCIAL_FIELDS: { key: FormKey; placeholder: string; icon: ReactNode }[] = [
  { key: 'website', placeholder: 'Website', icon: <Globe className="h-4 w-4 shrink-0 text-slate-500" aria-hidden /> },
  { key: 'facebook', placeholder: 'Facebook', icon: <Facebook className="h-4 w-4 shrink-0 text-blue-600" aria-hidden /> },
  { key: 'twitter', placeholder: 'Twitter / X', icon: <Twitter className="h-4 w-4 shrink-0 text-sky-500" aria-hidden /> },
  { key: 'instagramUrl', placeholder: 'Instagram', icon: <Instagram className="h-4 w-4 shrink-0 text-pink-500" aria-hidden /> },
  { key: 'pinterestUrl', placeholder: 'Pinterest', icon: <Pin className="h-4 w-4 shrink-0 text-red-600" aria-hidden /> },
  { key: 'linkedinUrl', placeholder: 'LinkedIn', icon: <Linkedin className="h-4 w-4 shrink-0 text-blue-700" aria-hidden /> },
  { key: 'tiktokUrl', placeholder: 'TikTok', icon: <Music className="h-4 w-4 shrink-0 text-slate-800" aria-hidden /> },
  { key: 'youtubeUrl', placeholder: 'YouTube', icon: <Youtube className="h-4 w-4 shrink-0 text-red-600" aria-hidden /> },
];

interface StaffChoice {
  value: number;
  label: string;
}

/** The "— suggested" hint while a recommended field is empty (never on a Locked page: nothing can fill it). */
function suggestedState(empty: boolean, locked = false): { state: FieldState; labelHint?: string } {
  return empty && !locked ? { state: 'warning', labelHint: '— suggested' } : { state: 'none' };
}

type SectionKey = 'social' | 'financial' | 'textNumbers' | 'rates' | 'ip';

/** Corp's Social URLs header for any section: closed until clicked, its "modified" pill shown while closed. */
function CollapsibleSection({
  title,
  open,
  onToggle,
  badge,
  bodyClassName = 'space-y-4 p-4',
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  badge?: ReactNode;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2 hover:bg-slate-100">
        <button
          type="button"
          className="flex flex-1 items-center justify-between gap-2 text-left text-sm font-medium text-slate-900"
          aria-expanded={open}
          onClick={onToggle}
          data-testid="facility-section-toggle"
        >
          <span>{title}</span>
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden /> : <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden />}
        </button>
        {badge}
      </div>
      {open && <div className={cn('border-t border-slate-200', bodyClassName)}>{children}</div>}
    </div>
  );
}

/** The two cost columns' colours, as the legacy modal's: operating room blue, anesthesia amber. */
const RATE_TONES = {
  blue: { box: 'border-blue-200 bg-blue-50', title: 'text-blue-900', head: 'bg-blue-50 text-blue-800' },
  amber: { box: 'border-amber-200 bg-amber-50', title: 'text-amber-900', head: 'bg-amber-50 text-amber-800' },
} as const;

/**
 * One cost column, as legacy's table: the hours are set by the Increment
 * (Generate rates, or Add row one increment past the last) and only the cost
 * is typed. Only the last row can be removed, so no gap opens in the scale. A
 * new row scrolls the list to the bottom, so it is never hidden. At Locked
 * the rows only show.
 */
function RateTable({
  tone,
  title,
  badge,
  pricing,
  name,
  rows,
  increment,
  locked,
  onChange,
}: {
  tone: keyof typeof RATE_TONES;
  title: string;
  badge?: ReactNode;
  /** The column's "$ per hours", for Generate rates; none at Locked. */
  pricing: ReactNode;
  /** "Operating room" / "Anesthesia": names each field for screen readers. */
  name: string;
  rows: RateRow[];
  increment: string;
  locked: boolean;
  onChange: (rows: RateRow[]) => void;
}) {
  const colors = RATE_TONES[tone];
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToEnd = useRef(false);
  useEffect(() => {
    if (!scrollToEnd.current) return;
    scrollToEnd.current = false;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [rows.length]);
  const setCost = (i: number, cost: string) => onChange(rows.map((r, j) => (j === i ? { ...r, cost } : r)));
  const testId = name.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className={cn('min-w-0 space-y-3 rounded-md border p-3', colors.box)}>
      <span className={cn(LABEL_CLASS, colors.title)}>
        <span>{title}</span>
        {badge}
      </span>
      {pricing}
      {rows.length === 0 ? (
        <p className={DESCRIPTION_CLASS}>{locked ? 'No rates.' : 'No rates yet. Generate them, or load them from another facility.'}</p>
      ) : (
        <div ref={listRef} className="max-h-72 space-y-1.5 overflow-y-auto" data-testid={`${testId}-rows`}>
          {/* Sticky, so a long list keeps its column names. */}
          <div className={cn('sticky top-0 z-10 flex items-center gap-2 py-0.5 text-xs font-medium', colors.head)}>
            <span className="w-12 shrink-0 text-right">Hours</span>
            <span className="pl-4">Cost</span>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="flex min-w-0 items-center gap-2">
              <span className="w-12 shrink-0 text-right text-sm tabular-nums text-slate-700" data-testid={`${testId}-hours`}>
                {row.hours}
              </span>
              <span className="text-sm text-slate-500">$</span>
              <Input
                aria-label={`${name} cost ${i + 1}`}
                inputMode="decimal"
                value={row.cost}
                onChange={(e) => setCost(i, e.target.value)}
                className="h-8"
                wrapperClassName="min-w-0 flex-1"
              />
              {!locked && (
                <span className="w-8 shrink-0">
                  {i === rows.length - 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Remove the last ${name.toLowerCase()} row`}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                      onClick={() => onChange(rows.slice(0, -1))}
                    />
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {!locked && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          aria-label={`Add ${name.toLowerCase()} row`}
          onClick={() => {
            scrollToEnd.current = true;
            onChange([...rows, nextRateRow(rows, increment)]);
          }}
        >
          Add row
        </Button>
      )}
    </div>
  );
}

const HOUR_STEP_SELECT = HOUR_STEP_OPTIONS.map((v) => ({ value: v, label: v }));

// ─── Modal ─────────────────────────────────────────────────────────────────────

export interface FacilityFormModalProps {
  /** The facility to edit; null to add one. */
  facility: FacilityRecord | null;
  /** The account's facilities, for Load Fees From Facility (the live ones other than this). */
  facilities?: FacilityRecord[];
  onClose: () => void;
  /** After a create or update; the page reloads the list. */
  onSaved: () => void;
  /** The image is assigned the moment it is picked (edit only). */
  onImageChanged?: (facilityId: number, url: string) => void;
}

export function FacilityFormModal({ facility, facilities = [], onClose, onSaved, onImageChanged }: FacilityFormModalProps) {
  const isNew = facility === null;
  const facilityId = facility?.facilityID ?? null;
  // Locked: a viewer — see the header comment.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<FacilityForm>(() => (facility ? formFromFacility(facility) : emptyFacilityForm()));
  // The form as loaded: what "modified" and Revert compare against.
  const [saved, setSaved] = useState<FacilityForm>(form);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(() => new Set());
  // Generate Rates' prices: never saved, as in legacy.
  const [generator, setGenerator] = useState({ orPrice: '', orPer: '1', anPrice: '', anPer: '1' });
  const [loadFromId, setLoadFromId] = useState<number | null>(null);

  const loadSources = useMemo(
    () => facilities.filter((f) => f.active && f.facilityID !== facilityId).sort((a, b) => a.name.localeCompare(b.name)),
    [facilities, facilityId],
  );

  const [countries, setCountries] = useState<{ value: number; label: string }[]>([]);
  const [countryZones, setCountryZones] = useState<TimezoneChoice[] | null>(null);
  const [staff, setStaff] = useState<{ doctors: StaffChoice[]; nurses: StaffChoice[]; coordinators: StaffChoice[] }>({
    doctors: [],
    nurses: [],
    coordinators: [],
  });

  const accountZones = useTimezones();
  const { titles } = useRoleTitles();

  // Countries, and the account's own country / timezone: a new facility starts
  // there, and an edit with no timezone of its own shows the account's (corp
  // does the same) — set on both sides so it only saves if someone changes it.
  useEffect(() => {
    let cancelled = false;
    listCountryCodes()
      .then((list) => {
        if (!cancelled) setCountries(list.filter((c) => c.countryID > 0).map((c) => ({ value: c.countryID, label: c.countryName })));
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load country codes');
      });
    if (isNew || form.timezoneID === null) {
      getAccountFacilityDefaults()
        .then(({ timezoneID, countryID }) => {
          if (cancelled) return;
          const fill = (p: FacilityForm): FacilityForm => ({
            ...p,
            timezoneID: p.timezoneID ?? timezoneID,
            countryID: isNew ? (p.countryID ?? countryID) : p.countryID,
          });
          setForm(fill);
          setSaved(fill);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
    // Once, when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The care team, keeping whoever the facility already has even if since deactivated.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listDoctors({ includeIds: toIncludeIds(facility?.doctorID) }),
      listNurses({ includeIds: toIncludeIds(facility?.nurseID) }),
      listCoordinators({ includeIds: toIncludeIds(facility?.coordID) }),
    ])
      .then(([d, n, c]) => {
        if (cancelled) return;
        setStaff({
          doctors: buildStaffOptions(d, (r) => r.doctorID, formatDoctorName),
          nurses: buildStaffOptions(n, (r) => r.nurseID, formatNurseName),
          coordinators: buildStaffOptions(c, (r) => r.coordID, formatCoordinatorName),
        });
      })
      .catch(() => {
        // The selects then offer only "None"; a saved assignment still saves back unchanged.
      });
    return () => {
      cancelled = true;
    };
  }, [facility?.doctorID, facility?.nurseID, facility?.coordID]);

  // The chosen country's timezones; a timezone outside a newly chosen country is cleared.
  useEffect(() => {
    const countryID = form.countryID;
    if (!countryID) {
      setCountryZones(null);
      return;
    }
    let cancelled = false;
    listCountryTimezones(countryID)
      .then((list) => {
        if (cancelled) return;
        setCountryZones(list);
        if (countryID !== saved.countryID) {
          setForm((p) => (p.timezoneID !== null && !list.some((z) => z.value === p.timezoneID) ? { ...p, timezoneID: null } : p));
        }
      })
      .catch(() => {
        if (!cancelled) setCountryZones([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.countryID, saved.countryID]);

  const timezoneOptions = useMemo(() => {
    const zones = countryZones ?? (accountZones.data ?? []).filter((z) => z.value !== 0).map((z) => ({ value: z.value, label: z.label }));
    return zones.map((z) => ({ value: z.value, label: z.label }));
  }, [countryZones, accountZones.data]);

  const changed = useMemo(() => (isNew ? new Set<FormKey>() : changedKeys(form, saved)), [isNew, form, saved]);
  // A new facility counts its picked image too: closing would lose it.
  const dirty = isNew ? !same(form, saved) : changed.size > 0;

  const missingName = form.name.trim() === '';
  const requiredErrors = missingName ? ['Facility name is required'] : [];

  function patch(next: Partial<FacilityForm>) {
    setForm((p) => ({ ...p, ...next }));
  }

  function revert(key: FormKey) {
    setForm((p) => ({ ...p, [key]: saved[key] }));
  }

  function badge(...keys: FormKey[]): ReactNode {
    const hit = keys.filter((k) => changed.has(k));
    if (hit.length === 0) return null;
    return (
      <ModifiedPill
        label={keys.length > 1 ? 'these fields' : FIELD_LABELS[keys[0]]}
        onRevert={() => hit.forEach(revert)}
      />
    );
  }

  function requestClose() {
    if (saving) return;
    if (dirty && !locked) setConfirmDiscard(true);
    else onClose();
  }

  async function handleSave() {
    if (requiredErrors.length > 0 || saving || locked) return;
    setSaving(true);
    try {
      if (isNew) {
        await createFacility(buildCreatePayload(form));
        toast.success('Facility created successfully');
      } else {
        await updateFacility(facilityId as number, buildUpdatePayload(form, saved));
        toast.success('Facility updated successfully');
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, isNew ? 'Failed to create facility.' : 'Failed to update facility. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  function setDay(day: (typeof DAYS_OF_WEEK)[number], next: Partial<OperatingHours[typeof day]>) {
    setForm((p) => ({ ...p, operatingHours: { ...p.operatingHours, [day]: { ...p.operatingHours[day], ...next } } }));
  }

  function toggleSection(key: SectionKey) {
    setOpenSections((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** Generating or loading rewrites both tables, so Rates opens to show them. */
  function showCostTables() {
    setOpenSections((s) => new Set([...s, 'rates']));
  }

  const canGenerate = Number(form.hourIncrement) > 0 && Number(form.maxHours) > 0;

  /** A cost column's "$ per hours", for Generate rates (not saved). */
  function ratePricing(label: string, price: 'orPrice' | 'anPrice', per: 'orPer' | 'anPer') {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-sm text-slate-500">$</span>
        <Input
          aria-label={`${label} price`}
          type="number"
          step="0.01"
          min="0"
          placeholder="0"
          value={generator[price]}
          onChange={(e) => setGenerator((g) => ({ ...g, [price]: e.target.value }))}
          className="h-8"
          wrapperClassName="min-w-0 flex-1"
        />
        <span className="text-sm text-slate-500">per</span>
        <label htmlFor={`facility-${per}`} className="sr-only">
          {label} per hours
        </label>
        <Select
          id={`facility-${per}`}
          options={HOUR_STEP_SELECT}
          value={generator[per]}
          onChange={(v) => v && setGenerator((g) => ({ ...g, [per]: v }))}
          wrapperClassName="w-20 shrink-0"
          className="h-8"
        />
        <span className="text-sm text-slate-500">hours</span>
      </div>
    );
  }

  function generateRates() {
    patch({
      orRates: generateRateRows(form.hourIncrement, form.maxHours, generator.orPrice, generator.orPer),
      anRates: generateRateRows(form.hourIncrement, form.maxHours, generator.anPrice, generator.anPer),
    });
    showCostTables();
  }

  function loadFees() {
    const source = loadSources.find((f) => f.facilityID === loadFromId);
    if (!source) return;
    patch({ orRates: source.orRates.map((r) => ({ ...r })), anRates: source.anRates.map((r) => ({ ...r })) });
    showCostTables();
    toast.success(`Rates loaded from ${source.name}`);
  }

  const teamRows: { key: 'doctorID' | 'nurseID' | 'coordID'; label: string; options: StaffChoice[] }[] = [
    { key: 'doctorID', label: titles.doctor ?? 'Doctor', options: staff.doctors },
    { key: 'nurseID', label: titles.nurse ?? 'Nurse', options: staff.nurses },
    { key: 'coordID', label: titles.coordinator ?? 'Coordinator', options: staff.coordinators },
  ];

  const footer = locked ? (
    <DialogFooterLayout>
      <div className="flex-1" />
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </DialogFooterLayout>
  ) : (
    <DialogFooterLayout errors={requiredErrors} isCreating={isNew}>
      {!isNew && dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={() => setForm({ ...saved })}
          >
            Revert
          </Button>
        </div>
      )}
      <div className="flex-1" />
      <Button variant="secondary" onClick={requestClose} disabled={saving}>
        Cancel
      </Button>
      <Button onClick={() => void handleSave()} loading={saving} disabled={missingName}>
        {isNew ? 'Create Facility' : 'Save changes'}
      </Button>
    </DialogFooterLayout>
  );

  const inputClass = (s: FieldState) => fieldClasses.input(s);

  const address2 = suggestedState(form.address2.trim() === '', locked);
  const city = suggestedState(form.city.trim() === '', locked);
  const stateField = suggestedState(form.state.trim() === '', locked);
  const postal = suggestedState(form.postalCode.trim() === '', locked);
  const country = suggestedState(!form.countryID, locked);
  const phone = suggestedState(form.phone.trim() === '', locked);
  const openDate = suggestedState(form.openDate === '', locked);

  return (
    <Modal
      isOpen
      onClose={requestClose}
      closeOnEscape={false}
      size="xl"
      title={
        <DialogHeading
          title={locked ? 'Facility' : isNew ? 'Create Facility' : 'Edit Facility'}
          variant={isNew || locked ? 'default' : 'edit'}
          description={
            isNew ? (
              'Add a facility to this account.'
            ) : (
              <>
                Facility ID: <span className="font-mono font-medium text-slate-900">{facilityId}</span>
              </>
            )
          }
        />
      }
      footer={footer}
    >
      <AdminLockedFieldset>
        <div className="grid gap-6">
          <div className="space-y-6">
            <div className="flex flex-col gap-6 md:flex-row">
              {/* Left: the image */}
              <div className="md:w-48 md:shrink-0">
                <EntityImageTile
                  entityType="facility"
                  entityId={facilityId}
                  imageUrl={form.facilityImage}
                  label="Facility Image"
                  suggested
                  onSelected={(url) => {
                    patch({ facilityImage: url });
                    // An existing facility's image is assigned on the spot, so it is not an unsaved change.
                    if (facilityId !== null) {
                      setSaved((p) => ({ ...p, facilityImage: url }));
                      onImageChanged?.(facilityId, url);
                    }
                  }}
                />
              </div>

              {/* Right: the key fields */}
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <SwitchField
                    id="facility-default"
                    label="Default"
                    checked={form.defaultFacility}
                    onChange={(v) => patch({ defaultFacility: v })}
                    badge={badge('defaultFacility')}
                  />
                  <div className="flex items-center gap-1">
                    {badge('facilityStatus')}
                    <label htmlFor="facility-status" className="sr-only">
                      Status
                    </label>
                    <Select<FacilityStatus>
                      id="facility-status"
                      options={FACILITY_STATUS_OPTIONS}
                      value={form.facilityStatus}
                      onChange={(v) => v && patch({ facilityStatus: v })}
                      wrapperClassName="w-36"
                      className={cn('h-8 border-0 text-xs font-medium', FACILITY_STATUS_CLASS[form.facilityStatus])}
                    />
                  </div>
                </div>

                <DialogField
                  label="Facility Name"
                  htmlFor="facility-name"
                  required
                  state={missingName ? 'error' : 'none'}
                  labelHint={missingName ? '— required' : undefined}
                  badge={badge('name')}
                >
                  <Input
                    id="facility-name"
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    className={inputClass(missingName ? 'error' : 'none')}
                  />
                </DialogField>

                <DialogField label="Address" htmlFor="facility-address2" {...address2} badge={badge('address2')}>
                  <Input
                    id="facility-address2"
                    value={form.address2}
                    placeholder="Suite, unit, etc."
                    onChange={(e) => patch({ address2: e.target.value })}
                    className={inputClass(address2.state)}
                  />
                </DialogField>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
                  <DialogField label="City" htmlFor="facility-city" {...city} badge={badge('city')}>
                    <Input id="facility-city" value={form.city} onChange={(e) => patch({ city: e.target.value })} className={inputClass(city.state)} />
                  </DialogField>
                  <DialogField label="State" htmlFor="facility-state" {...stateField} badge={badge('state')}>
                    <Input id="facility-state" value={form.state} onChange={(e) => patch({ state: e.target.value })} className={inputClass(stateField.state)} />
                  </DialogField>
                  <DialogField label="Postal Code" htmlFor="facility-postal" {...postal} badge={badge('postalCode')}>
                    <Input
                      id="facility-postal"
                      value={form.postalCode}
                      onChange={(e) => patch({ postalCode: e.target.value })}
                      className={inputClass(postal.state)}
                    />
                  </DialogField>
                  <DialogField label="Country" htmlFor="facility-country" {...country} badge={badge('countryID')}>
                    <Select<number>
                      id="facility-country"
                      options={countries}
                      value={form.countryID}
                      onChange={(v) => v && patch({ countryID: v })}
                      placeholder="Select"
                      searchable
                      className={inputClass(country.state)}
                    />
                  </DialogField>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <DialogField label="Phone Number" htmlFor="facility-phone" {...phone} badge={badge('phone')}>
                    <Input id="facility-phone" value={form.phone} onChange={(e) => patch({ phone: e.target.value })} className={inputClass(phone.state)} />
                  </DialogField>
                  <DialogField label="Timezone" htmlFor="facility-timezone" badge={badge('timezoneID')}>
                    <Select<number>
                      id="facility-timezone"
                      options={timezoneOptions}
                      value={form.timezoneID}
                      onChange={(v) => v && patch({ timezoneID: v })}
                      placeholder="Select timezone"
                      searchable
                    />
                  </DialogField>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <DialogField label="Facility type" htmlFor="facility-type" badge={badge('facilityType')}>
                  <Select<number>
                    id="facility-type"
                    options={FACILITY_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={form.facilityType}
                    onChange={(v) => v !== null && patch({ facilityType: v })}
                  />
                </DialogField>
                <DialogField label="Open date" htmlFor="facility-open-date" {...openDate} badge={badge('openDate')}>
                  <DatePicker
                    id="facility-open-date"
                    value={form.openDate}
                    onChange={(v) => patch({ openDate: v })}
                    clearable
                    className={inputClass(openDate.state)}
                  />
                </DialogField>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <DialogField label="Soft open start" htmlFor="facility-soft-open-start" badge={badge('softOpenStart')}>
                  <DatePicker id="facility-soft-open-start" value={form.softOpenStart} onChange={(v) => patch({ softOpenStart: v })} clearable />
                </DialogField>
                <DialogField label="Soft open stop" htmlFor="facility-soft-open-stop" badge={badge('softOpenStop')}>
                  <DatePicker id="facility-soft-open-stop" value={form.softOpenStop} onChange={(v) => patch({ softOpenStop: v })} clearable />
                </DialogField>
              </div>

              {/* Social URLs & Website: collapsed until opened, as corp. */}
              <CollapsibleSection
                title="Social URLs & Website"
                open={openSections.has('social')}
                onToggle={() => toggleSection('social')}
                badge={badge(...SOCIAL_KEYS)}
                bodyClassName="space-y-2 p-3"
              >
                {SOCIAL_FIELDS.map(({ key, placeholder, icon }) => (
                  <div key={key} className="flex items-center gap-2">
                    {icon}
                    <Input
                      aria-label={placeholder}
                      placeholder={placeholder}
                      type={key === 'website' ? 'url' : 'text'}
                      value={form[key] as string}
                      onChange={(e) => patch({ [key]: e.target.value } as Partial<FacilityForm>)}
                      className="h-8"
                      wrapperClassName="w-full"
                    />
                  </div>
                ))}
              </CollapsibleSection>

              {/* Operating Hours */}
              <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={LABEL_CLASS}>
                    <span>Operating Hours</span>
                    {badge('operatingHours')}
                  </span>
                  <SwitchField
                    id="facility-apply-hours"
                    label="Apply to calendar"
                    checked={form.applyHoursToCalendar}
                    onChange={(v) => patch({ applyHoursToCalendar: v })}
                    badge={badge('applyHoursToCalendar')}
                  />
                </div>
                <div className="space-y-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const h = form.operatingHours[day];
                    const open = !h.closed;
                    const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
                    return (
                      <div key={day} className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`hours-${day}`}>
                        <span className="w-24 text-sm text-slate-700">{dayLabel}</span>
                        <Switch checked={open} onChange={(v) => setDay(day, { closed: !v })} aria-label={`${dayLabel} open`} />
                        <span className="w-12 text-xs text-slate-600">{open ? 'Open' : 'Closed'}</span>
                        {locked ? (
                          // TimeField has no read-only mode: a Locked page shows the times as text.
                          open && (
                            <>
                              <span className="w-36 text-sm tabular-nums text-slate-900">{formatTime12h(h.open)}</span>
                              <span className="text-sm text-slate-500">to</span>
                              <span className="w-36 text-sm tabular-nums text-slate-900">{formatTime12h(h.close)}</span>
                            </>
                          )
                        ) : (
                          <>
                            <TimeField
                              value={open ? h.open : ''}
                              onChange={(v) => setDay(day, { open: v })}
                              disabled={!open}
                              ariaLabel={`${dayLabel} opens`}
                              wrapperClassName="w-36"
                            />
                            <span className="text-sm text-slate-500">to</span>
                            <TimeField
                              value={open ? h.close : ''}
                              onChange={(v) => setDay(day, { close: v })}
                              disabled={!open}
                              ariaLabel={`${dayLabel} closes`}
                              wrapperClassName="w-36"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Care Team, labelled with the account's role titles */}
              <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-4">
                <div className="text-sm font-medium text-slate-900">Care Team</div>
                <div className="grid gap-4 md:grid-cols-3">
                  {teamRows.map(({ key, label, options }) => (
                    <DialogField key={key} label={label} htmlFor={`facility-${key}`} badge={badge(key)}>
                      <SearchableCombobox<number>
                        id={`facility-${key}`}
                        options={[{ value: 0, label: 'None' }, ...options]}
                        value={form[key]}
                        onChange={(v) => patch({ [key]: v ?? 0 } as Partial<FacilityForm>)}
                        placeholder="None"
                      />
                    </DialogField>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <DialogField label="Shortcode Address" htmlFor="facility-shortcode-address" badge={badge('address')}>
                <p className={cn(DESCRIPTION_CLASS, '-mt-1')}>This address is used in shortcode variables.</p>
                <Textarea id="facility-shortcode-address" rows={2} value={form.address} onChange={(e) => patch({ address: e.target.value })} />
              </DialogField>
              <DialogField label="Hint" htmlFor="facility-hint" badge={badge('hint')}>
                <Textarea id="facility-hint" rows={2} value={form.hint} onChange={(e) => patch({ hint: e.target.value })} />
              </DialogField>
            </div>
          </div>

          <CollapsibleSection
            title="Financial and Display Settings"
            open={openSections.has('financial')}
            onToggle={() => toggleSection('financial')}
            badge={badge(...FINANCIAL_KEYS)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <DialogField label="Sales Tax Rate" htmlFor="facility-tax-rate" badge={badge('taxRate')}>
                <div className="flex items-center gap-1">
                  <Input
                    id="facility-tax-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.taxRate}
                    onChange={(e) => patch({ taxRate: e.target.value })}
                    wrapperClassName="w-24"
                  />
                  <span className="text-sm text-slate-500">%</span>
                </div>
              </DialogField>
              {(['onQuotes', 'nonAdminAllowEdit', 'onNewLeadForm'] as const).map((key) => (
                <DialogField key={key} label={FIELD_LABELS[key]} htmlFor={`facility-${key}`} badge={badge(key)}>
                  <Select
                    id={`facility-${key}`}
                    options={YES_NO}
                    value={form[key] ? '1' : '0'}
                    onChange={(v) => v && patch({ [key]: v === '1' } as Partial<FacilityForm>)}
                  />
                </DialogField>
              ))}
              <DialogField label="Facility Rates" htmlFor="facility-rate-type" badge={badge('facilityRateType')}>
                <Select<number>
                  id="facility-rate-type"
                  options={FACILITY_RATE_TYPES.map((o) => ({ value: o.value, label: o.label }))}
                  value={form.facilityRateType}
                  onChange={(v) => v !== null && patch({ facilityRateType: v })}
                />
              </DialogField>
            </div>
          </CollapsibleSection>

          {/* Assigned and removed at once, apart from Save; view-only at Locked. Loads when opened. */}
          {facility !== null && (
            <CollapsibleSection title="Text Numbers" open={openSections.has('textNumbers')} onToggle={() => toggleSection('textNumbers')}>
              <FacilityTextNumbers facilityId={facility.facilityID} facilityName={facility.name} readOnly={locked} />
            </CollapsibleSection>
          )}

          {/* Rates: legacy's generator, then the cost tables (operating room left,
              anesthesia right). The prices are not saved; Increment, Max and the tables are. */}
          <CollapsibleSection
            title="Rates"
            open={openSections.has('rates')}
            onToggle={() => toggleSection('rates')}
            badge={badge('hourIncrement', 'maxHours', 'orRates', 'anRates')}
          >
            <div className="flex flex-wrap items-end gap-4">
              <DialogField label="Increment" htmlFor="facility-hour-increment" badge={badge('hourIncrement')}>
                <Select
                  id="facility-hour-increment"
                  options={
                    HOUR_STEP_OPTIONS.includes(form.hourIncrement)
                      ? HOUR_STEP_SELECT
                      : [...HOUR_STEP_SELECT, { value: form.hourIncrement, label: form.hourIncrement }]
                  }
                  value={form.hourIncrement}
                  onChange={(v) => v && patch({ hourIncrement: v })}
                  wrapperClassName="w-24"
                />
              </DialogField>
              <DialogField label="Max" htmlFor="facility-max-hours" badge={badge('maxHours')}>
                <div className="flex items-center gap-1">
                  <Input
                    id="facility-max-hours"
                    inputMode="decimal"
                    maxLength={5}
                    placeholder="8"
                    value={form.maxHours}
                    onChange={(e) => patch({ maxHours: e.target.value })}
                    wrapperClassName="w-20"
                  />
                  <span className="text-sm text-slate-500">hours</span>
                </div>
              </DialogField>
              {!locked && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={generateRates}
                  disabled={!canGenerate}
                  title={canGenerate ? undefined : 'Set the Max hours first'}
                >
                  Generate rates
                </Button>
              )}
              {/* Load Fees From Facility: copies another live facility's cost tables. */}
              {!locked && loadSources.length > 0 && (
                <div className="flex min-w-0 flex-wrap items-end gap-2 border-l border-slate-200 pl-4">
                  <DialogField label="Load fees from facility" htmlFor="facility-load-from" className="w-56">
                    <Select<number>
                      id="facility-load-from"
                      options={loadSources.map((f) => ({ value: f.facilityID, label: f.name }))}
                      value={loadFromId}
                      onChange={(v) => setLoadFromId(v)}
                      placeholder="Select one..."
                      searchable
                    />
                  </DialogField>
                  <Button variant="secondary" size="sm" onClick={loadFees} disabled={loadFromId === null}>
                    Load
                  </Button>
                </div>
              )}
            </div>
            {!locked && (
              <p className={DESCRIPTION_CLASS}>
                Generate rates fills both cost tables from each column&apos;s price: one row per increment up to the max, each started block of hours at the full price.
                {loadSources.length > 0 && ' Load replaces both with the chosen facility’s.'}
              </p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <RateTable
                tone="blue"
                title="Operating Room"
                badge={badge('orRates')}
                pricing={locked ? null : ratePricing('Operating Room', 'orPrice', 'orPer')}
                name="Operating room"
                rows={form.orRates}
                increment={form.hourIncrement}
                locked={locked}
                onChange={(rows) => patch({ orRates: rows })}
              />
              <RateTable
                tone="amber"
                title="Anesthesia"
                badge={badge('anRates')}
                pricing={locked ? null : ratePricing('Anesthesia', 'anPrice', 'anPer')}
                name="Anesthesia"
                rows={form.anRates}
                increment={form.hourIncrement}
                locked={locked}
                onChange={(rows) => patch({ anRates: rows })}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="IP Addresses" open={openSections.has('ip')} onToggle={() => toggleSection('ip')} badge={badge('ipAddresses')}>
            <p className={DESCRIPTION_CLASS}>
              {locked
                ? form.ipAddresses.length > 0
                  ? 'Allowed IP addresses for this facility.'
                  : 'No allowed IP addresses for this facility.'
                : 'Add allowed IP addresses for this facility.'}
            </p>
            <div className="max-h-40 space-y-2 overflow-y-auto">
              {form.ipAddresses.map((ip, i) => (
                <div key={i} className="flex min-w-0 items-center gap-2">
                  <Input
                    aria-label={`IP address ${i + 1}`}
                    placeholder="e.g. 192.168.1.1"
                    value={ip}
                    onChange={(e) => patch({ ipAddresses: form.ipAddresses.map((a, j) => (j === i ? e.target.value : a)) })}
                    className="font-mono"
                    wrapperClassName="min-w-0 flex-1"
                  />
                  {!locked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Remove IP address ${i + 1}`}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                      onClick={() => patch({ ipAddresses: form.ipAddresses.filter((_, j) => j !== i) })}
                    />
                  )}
                </div>
              ))}
              {!locked && (
                <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => patch({ ipAddresses: [...form.ipAddresses, ''] })}>
                  Add IP address
                </Button>
              )}
            </div>
          </CollapsibleSection>
        </div>
      </AdminLockedFieldset>

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
        title="Discard changes?"
        message="You have unsaved changes. Close without saving them?"
        confirmLabel="Discard changes"
        variant="warning"
      />
    </Modal>
  );
}
